import { Grid, Stack, Text, Title } from '@mantine/core';
import moment from 'moment';

import { EORIGIN_DATE_FORMAT_WITH_TIME, Incident } from '@e-origin/shared';

import { TextInput } from '@components';

interface TransitViewIncidentProps {
  incident: Incident;
  index: number;
}

export const TransitViewIncident: React.FC<TransitViewIncidentProps> = ({ incident, index }) => {
  return (
    <Stack key={incident.id} py="md" gap="xl">
      <Title order={3}>#{index + 1} Incident</Title>

      <Grid>
        <Grid.Col span={3}>
          <TextInput value={incident.code} label="Code" disabled />
        </Grid.Col>
        <Grid.Col span={3}>
          <TextInput value={incident.text} label="Additional explanation" disabled />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={4}>
          <TextInput value={incident.officeOfIncidentRegistrationReference} label="Office of incident registration reference" disabled />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            value={moment(incident.incidentNotificationDateAndTime).format(EORIGIN_DATE_FORMAT_WITH_TIME)}
            label="Incident notification date"
            disabled
          />
        </Grid.Col>
      </Grid>

      {incident.Endorsement && (
        <Stack>
          <Title order={5} fw={600}>
            Endorsement
          </Title>
          <Grid>
            <Grid.Col span={3}>
              <TextInput value={incident.Endorsement?.date} label="Date" disabled />
            </Grid.Col>
            <Grid.Col span={3}>
              <TextInput value={incident.Endorsement?.authority} label="Authority" disabled />
            </Grid.Col>
            <Grid.Col span={3}>
              <TextInput value={incident.Endorsement?.place} label="Place" disabled />
            </Grid.Col>
            <Grid.Col span={3}>
              <TextInput value={incident.Endorsement?.country} label="Country" disabled />
            </Grid.Col>
          </Grid>
        </Stack>
      )}

      {incident.Location && (
        <Stack>
          <Title order={5} fw={600}>
            Location
          </Title>
          <Grid>
            <Grid.Col span={3}>
              <TextInput value={incident.Location?.qualifierOfIdentification} label="Qualifier of Identification" disabled />
            </Grid.Col>
            <Grid.Col span={3}>
              <TextInput value={incident.Location?.UNLocode} label="UNLocode" disabled />
            </Grid.Col>
            <Grid.Col span={3}>
              <TextInput value={incident.Location?.country} label="Country" disabled />
            </Grid.Col>
          </Grid>

          {incident.Location.GNSS && (
            <Grid>
              <Grid.Col span={3}>
                <TextInput value={incident.Location?.GNSS?.latitude} label="Latitude" disabled />
              </Grid.Col>
              <Grid.Col span={3}>
                <TextInput value={incident.Location?.GNSS?.longitude} label="Longitude" disabled />
              </Grid.Col>
            </Grid>
          )}

          {incident.Location.Address && (
            <Grid>
              <Grid.Col span={3}>
                <TextInput value={incident.Location?.Address?.streetAndNumber} label="Street and Number" disabled />
              </Grid.Col>
              <Grid.Col span={3}>
                <TextInput value={incident.Location?.Address?.city} label="City" disabled />
              </Grid.Col>
              <Grid.Col span={3}>
                <TextInput value={incident.Location?.Address?.postcode} label="Postal code" disabled />
              </Grid.Col>
            </Grid>
          )}
        </Stack>
      )}

      {incident.TransportEquipment?.length && (
        <Stack>
          <Title order={5} fw={600}>
            Transport Equipment
          </Title>
          {incident.TransportEquipment.map((equipment, equipmentIdx) => (
            <Stack key={equipmentIdx}>
              {incident.TransportEquipment?.length > 1 && (
                <Text key={`title-${equipmentIdx}`}>{`Transport Equipment ${`#${equipment.sequenceNumber || equipmentIdx}`}`}</Text>
              )}
              <Stack key={`details-${equipmentIdx}`}>
                <Grid>
                  <Grid.Col span={3}>
                    <TextInput value={equipment.containerIdentificationNumber} label="Container Number" disabled />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <TextInput value={equipment.numberOfSeals} label="Number of Seals" disabled />
                  </Grid.Col>
                </Grid>

                {equipment.Seal?.length > 0 && (
                  <Grid>
                    {equipment.Seal.map((seal, sealIndex) => (
                      <Grid.Col span={3} key={`seal-${sealIndex}`}>
                        <TextInput
                          key={seal.sequenceNumber || sealIndex}
                          value={seal.identifier}
                          label={`Seal Identifier${equipment.Seal?.length > 1 ? ` #${seal.sequenceNumber || sealIndex}` : ''}`}
                          disabled
                        />
                      </Grid.Col>
                    ))}
                  </Grid>
                )}

                {equipment.GoodsReference?.length > 0 && (
                  <Grid>
                    {equipment.GoodsReference.map((goodsReference, goodsReferenceIndex) => (
                      <Grid.Col span={3} key={`goodsReference-${goodsReferenceIndex}`}>
                        <TextInput
                          key={goodsReference.declarationGoodsItemNumber || goodsReferenceIndex}
                          value={goodsReference.declarationGoodsItemNumber}
                          label={`Goods Reference${equipment.GoodsReference?.length > 1 ? ` #${goodsReference.sequenceNumber || goodsReferenceIndex}` : ''}`}
                          disabled
                        />
                      </Grid.Col>
                    ))}
                  </Grid>
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}

      {incident.Transhipment && (
        <Stack>
          <Title order={5} fw={600}>
            Transhipment
          </Title>
          <Grid>
            <Grid.Col span={3}>
              <TextInput value={incident.Transhipment?.containerIndicator} label="Container Indicator" disabled />
            </Grid.Col>
            {incident.Transhipment?.TransportMeans && (
              <>
                <Grid.Col span={3}>
                  <TextInput value={incident.Transhipment?.TransportMeans?.typeOfIdentification} label="Transport Means ID Type" disabled />
                </Grid.Col>
                <Grid.Col span={3}>
                  <TextInput value={incident.Transhipment?.TransportMeans?.identificationNumber} label="Transport Means ID" disabled />
                </Grid.Col>
                <Grid.Col span={3}>
                  <TextInput value={incident.Transhipment?.TransportMeans?.nationality} label="Transport Means Nationality" disabled />
                </Grid.Col>
              </>
            )}
          </Grid>
        </Stack>
      )}
    </Stack>
  );
};
